/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ProductCardProps } from '@coursera/cds-core';
import { Grid, ProductCard, VisuallyHidden } from '@coursera/cds-core';

import { PRODUCT_CARD_EXPERIMENT_VARIANT } from 'bundles/product-card/constants';
import { getSearchResultDesktopCardSize } from 'bundles/search-common/components/search-cards/cardLayout';
import {
  type SearchResultInlineModule,
  getInlineModuleInsertionPoints,
} from 'bundles/search-common/components/search-cards/inlineModules';

import _t from 'i18n!nls/search-common';

const styles = {
  productCard: css`
    padding: 0 var(--cds-spacing-100) var(--cds-spacing-300);
  `,
};

type CdsCardsProps = {
  resultsPerPage: number;
  variant?: ProductCardProps['variant'];
  coachOpen?: boolean;
  enableStickyFilterBar?: boolean;
  inlineModules?: SearchResultInlineModule[];
};

export const ProductCardV2Placeholder = ({
  variant = 'grid',
  experimentVariant = PRODUCT_CARD_EXPERIMENT_VARIANT,
  alwaysSmallImage,
}: {
  variant?: ProductCardProps['variant'];
  experimentVariant?: ProductCardProps['experimentVariant'];
  alwaysSmallImage?: boolean;
}) => {
  const placeholderProps: ProductCardProps = {
    variant,
    productType: ' ',
    experimentVariant,
    alwaysSmallImage,
  };

  return <ProductCard {...placeholderProps} loading={true} />;
};

export const ProductsCardsCdsPlaceholder = ({
  resultsPerPage,
  variant,
  coachOpen = false,
  enableStickyFilterBar = false,
  inlineModules,
}: CdsCardsProps) => {
  const searchCards = Array.from({ length: resultsPerPage }, (v, i) => i);
  const inlineModulesByCardCount = getInlineModuleInsertionPoints(inlineModules, searchCards.length);

  const desktopCardSize =
    variant === 'list' ? 12 : getSearchResultDesktopCardSize({ enableStickyFilterBar, coachOpen });

  return (
    <Grid container>
      <VisuallyHidden role="status">{_t('Loading search results')}</VisuallyHidden>

      {searchCards.map((placeholderIndex) => (
        <React.Fragment key={placeholderIndex}>
          <Grid
            item
            lg={desktopCardSize}
            md={variant === 'list' ? 12 : 4}
            sm={variant === 'list' ? 12 : 6}
            xs={12}
            css={styles.productCard}
          >
            <ProductCardV2Placeholder variant={variant} />
          </Grid>
          {inlineModulesByCardCount.get(placeholderIndex + 1)?.map((inlineModule) => (
            <Grid key={inlineModule.id} item xs={12}>
              {inlineModule.content}
            </Grid>
          ))}
        </React.Fragment>
      ))}
    </Grid>
  );
};
